import { getEffectiveMembershipStatus } from "../../../utils/membershipStatus";

export function formatMembershipDetail(membership) {
  if (!membership) {
    return { title: "Нет абонемента", meta: "Выдайте абонемент в карточке клиента" };
  }
  const title = membership.plan_name || "Абонемент";
  const parts = [];
  const today = new Date().toISOString().slice(0, 10);
  const effective = getEffectiveMembershipStatus(membership);
  const frozen = effective === "frozen";

  if (effective === "expired") {
    if (membership.ends_at) {
      parts.push(`истёк ${formatDateShort(membership.ends_at)}`);
    } else {
      parts.push("истёк");
    }
    return { title, meta: parts.join(" · ") };
  }

  if (membership.plan_type === "visits" && membership.visits_remaining != null) {
    parts.push(`${membership.visits_remaining} зан. осталось`);
  }
  if (frozen && membership.frozen_until) {
    parts.push(`заморожен до ${formatDateShort(membership.frozen_until)}`);
  }
  if (membership.ends_at) {
    parts.push(
      frozen
        ? `действует до ${formatDateShort(membership.ends_at)}`
        : `активен до ${formatDateShort(membership.ends_at)}`,
    );
  } else if (membership.plan_type !== "visits" && !frozen) {
    parts.push("без срока");
  }
  if (effective === "pending") {
    parts.push(
      membershipEffectivePrice(membership) <= 0
        ? "ожидает активации"
        : "ожидает оплаты",
    );
  } else if (
    effective === "active" &&
    membership.amount_due != null &&
    Number(membership.amount_due) > 0
  ) {
    parts.push(`долг ${Math.round(Number(membership.amount_due))} ₽`);
  } else if (!frozen && effective && effective !== "active") {
    parts.push(membershipStatusLabel(effective));
  }
  return {
    title,
    meta: parts.join(" · ") || (frozen ? "Заморожен" : "Активен"),
  };
}

export function membershipStatusLabel(status, notes) {
  const resolved =
    status && typeof status === "object"
      ? getEffectiveMembershipStatus(status)
      : status;
  if (
    resolved === "cancelled" &&
    notes &&
    (/Продление:\s*\+/i.test(notes) ||
      /Пополнение применено/i.test(notes) ||
      /зачислен/i.test(notes))
  ) {
    return "Зачислен";
  }
  const map = {
    pending: "Ожидает оплаты",
    active: "Активен",
    expired: "Истёк",
    frozen: "Заморожен",
    cancelled: "Отменён",
  };
  return map[resolved] || resolved;
}

/** Подпись pending с учётом бесплатного тарифа и договорной цены. */
export function pendingMembershipLabel(membership) {
  if (membershipEffectivePrice(membership) <= 0) {
    return "Ожидает активации";
  }
  return "Ожидает оплаты";
}

export function membershipStatusTone(status, notes) {
  const resolved =
    status && typeof status === "object"
      ? getEffectiveMembershipStatus(status)
      : status;
  if (
    resolved === "cancelled" &&
    notes &&
    (/Продление:\s*\+/i.test(notes) || /Пополнение применено/i.test(notes))
  ) {
    return "success";
  }
  if (resolved === "active") return "ok";
  if (resolved === "pending") return "warn";
  if (resolved === "frozen") return "warn";
  if (resolved === "expired") return "danger";
  return "muted";
}

export function paymentMethodLabel(method) {
  const map = {
    cash: "Наличные",
    card_transfer: "Перевод",
    online: "Онлайн",
    other: "Другое",
  };
  return map[method] || method || "—";
}

export function isActiveMembershipPayment(payment) {
  return payment && String(payment.status || "posted") !== "voided";
}

/** Ручная отметка в кассе — её можно удалить (не онлайн-эквайринг). */
export function canVoidManualPayment(payment) {
  if (!isActiveMembershipPayment(payment)) return false;
  if (payment.can_void === false) return false;
  if (payment.can_void === true) return true;
  // Эквайринг: есть intent или external id. Способ «online» без них — можно удалить.
  if (payment.payment_intent_id) return false;
  if (payment.external_payment_id) return false;
  return true;
}

export function formatAttendanceSummary(att) {
  if (!att || !att.total) {
    return { label: "—", hint: "Нет занятий в этом месяце", pct: null };
  }
  const pct =
    att.pct != null
      ? att.pct
      : Math.round((Number(att.present || 0) / att.total) * 100);
  return {
    label: `${att.present} из ${att.total}`,
    hint: `${pct}% посещаемость`,
    pct,
  };
}

export const COMPETITION_STATUS_LABELS = {
  pending: "На рассмотрении",
  approved: "Допущен",
  rejected: "Отклонён",
  cancelled: "Отмена",
};

export function competitionResultLabel(reg) {
  if (reg?.placement) {
    const n = Number(reg.placement);
    if (n === 1) return "1 место";
    if (n === 2) return "2 место";
    if (n === 3) return "3 место";
    return `${n} место`;
  }
  if (reg?.result_status === "participated") return "Участвовал";
  if (reg?.result_status === "no_show") return "Не явился";
  return null;
}

export function pollAnswerLabel(row) {
  const id = row?.option_id || row?.answers?.option_id;
  if (id === "coming" || id === "yes") return "Придёт";
  if (id === "not_coming" || id === "no") return "Не придёт";
  if (id === "maybe") return "Под вопросом";
  return id ? String(id) : "Ответил";
}

export function pollAnswerTone(row) {
  const id = row?.option_id || row?.answers?.option_id;
  if (id === "coming" || id === "yes") return "ok";
  if (id === "not_coming" || id === "no") return "muted";
  if (id === "maybe") return "warn";
  return "default";
}

export function formatDateShort(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = String(isoDate).slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

/** Договорная цена абонемента или цена тарифа. */
export function membershipEffectivePrice(membership) {
  if (membership == null) return 0;
  if (membership.price != null && membership.price !== "") {
    const n = Number(membership.price);
    return Number.isFinite(n) ? n : 0;
  }
  const planPrice = membership.plan?.price ?? membership.plan_price;
  const n = Number(planPrice ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Подпись цены тарифа в списках. */
export function planPriceLabel(plan, fmtMoneyFn) {
  if (plan?.price_on_issue) return "цена при выдаче";
  return fmtMoneyFn ? fmtMoneyFn(plan?.price ?? 0) : String(plan?.price ?? 0);
}

/** Одна строка под именем в карточке клиента */
export function clientProfileSubtitle(sport) {
  if (!sport) return "Без группы";
  const parts = [];
  const childCount = sport.children?.length || 0;
  if (sport.also_guardian || sport.account_kind === "guardian") {
    if (childCount) {
      parts.push(
        childCount === 1 ? "1 ребёнок" : `${childCount} ${childCount < 5 ? "ребёнка" : "детей"}`,
      );
    } else if (sport.account_kind === "guardian") {
      return "Родитель";
    }
  }
  if (sport.groups?.length) parts.push(sport.groups[0]);
  const m = sport.membership;
  if (m?.status === "pending") {
    parts.push("абонемент не оплачен");
  } else if (Number(m?.amount_due) > 0) {
    parts.push(m.plan_name || "абонемент");
    parts.push(`долг ${Math.round(Number(m.amount_due))} ₽`);
  } else if (
    m?.status === "frozen" ||
    (m?.frozen_until &&
      String(m.frozen_until).slice(0, 10) >=
        new Date().toISOString().slice(0, 10))
  ) {
    parts.push(m.plan_name || "абонемент");
    if (m.frozen_until) {
      parts.push(`заморожен до ${formatDateShort(m.frozen_until)}`);
    } else {
      parts.push("заморожен");
    }
  } else if (m?.plan_name) {
    parts.push(m.plan_name);
    if (m.visits_remaining != null) {
      parts.push(`${m.visits_remaining} зан.`);
    }
  }
  return parts.length ? parts.join(" · ") : "Без группы";
}

export function clientStatusBadges(sport) {
  const badges = [];
  if (!sport) return badges;
  if (sport.registration_status === "pending") {
    badges.push({ tone: "warn", label: "На модерации" });
  }
  if (sport.is_archived) badges.push({ tone: "muted", label: "Архив" });
  if (
    sport.warnings?.includes("unpaid_membership") ||
    sport.membership?.status === "pending" ||
    Number(sport.membership?.amount_due) > 0
  ) {
    badges.push({ tone: "warn", label: "Должник" });
  } else if (
    sport.membership?.status === "frozen" ||
    (sport.membership?.frozen_until &&
      String(sport.membership.frozen_until).slice(0, 10) >=
        new Date().toISOString().slice(0, 10))
  ) {
    badges.push({ tone: "warn", label: "Заморожен" });
  } else if (
    sport.warnings?.includes("membership_expired") ||
    sport.membership?.status === "expired"
  ) {
    badges.push({ tone: "danger", label: "Истёк" });
  } else if (sport.warnings?.includes("debtor")) {
    badges.push({ tone: "danger", label: "Нет абонемента" });
  } else if (sport.warnings?.includes("membership_expiring")) {
    badges.push({ tone: "warn", label: "Заканчивается" });
  }
  return badges;
}
